import React, { useCallback, useEffect, useState } from "react";
import { Bell, Receipt, Upload } from "lucide-react";
import { toast } from "sonner";
import { BotonUpim, EncabezadoUpim, SeccionUpim, TarjetaUpim } from "../../components/ui-upim";
import { getToken } from "../../services/api";

const API = process.env.REACT_APP_BACKEND_URL;
const ESTADOS = ["", "pendiente_datos", "lista_revision", "notificada_contador", "facturada",
  "requiere_correccion", "cancelada", "reembolsada"];

const auth = () => ({ Authorization: `Bearer ${getToken() || ""}` });

export default function BandejaFacturacionPage() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [estado, setEstado] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [uuid, setUuid] = useState("");
  const [xml, setXml] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [sinPermiso, setSinPermiso] = useState(false);

  const cargar = useCallback(async () => {
    const r = await fetch(`${API}/api/facturacion/solicitudes${estado ? `?estado=${estado}` : ""}`,
      { headers: auth() });
    if (r.status === 403) { setSinPermiso(true); return; }
    if (r.ok) { const d = await r.json(); setSolicitudes(d.solicitudes || []); }
  }, [estado]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async (id) => {
    const r = await fetch(`${API}/api/facturacion/solicitudes/${id}`, { headers: auth() });
    if (r.ok) { setDetalle(await r.json()); setUuid(""); setXml(null); setPdf(null); }
  };

  const notificar = async (id) => {
    const r = await fetch(`${API}/api/facturacion/solicitudes/${id}/notificar`,
      { method: "POST", headers: auth() });
    const d = await r.json().catch(() => ({}));
    if (r.ok) {
      toast.success(d.modo === "dry_run"
        ? "Registrada en la bandeja interna (WhatsApp de grupos no habilitado)"
        : "Contador notificado");
      cargar(); abrir(id);
    } else toast.error(d?.detail || "No se pudo notificar");
  };

  const cargarCfdi = async (id) => {
    if (!xml || !pdf || !uuid) { toast.error("Adjunta XML, PDF y captura el UUID"); return; }
    const fd = new FormData();
    fd.append("xml", xml); fd.append("pdf", pdf); fd.append("uuid_cfdi", uuid);
    const r = await fetch(`${API}/api/facturacion/solicitudes/${id}/cfdi`,
      { method: "POST", headers: auth(), body: fd });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success("CFDI registrado"); cargar(); abrir(id); return; }
    const lista = d?.detail?.errores || [d?.detail || "No se pudo registrar el CFDI"];
    lista.forEach((e) => toast.error(e));
  };

  if (sinPermiso) {
    return (
      <SeccionUpim testId="bandeja-facturacion-sin-permiso" className="min-h-screen py-24">
        <EncabezadoUpim etiqueta="Facturación" titulo="Acceso restringido"
          descripcion="Esta bandeja es exclusiva del rol Contador y de la Administración Nacional." />
      </SeccionUpim>
    );
  }

  const campo = "rounded-lg border border-white/10 bg-[var(--upim-superficie-2)] px-3 py-2 text-sm text-[var(--upim-texto)]";

  return (
    <SeccionUpim testId="bandeja-facturacion-page" className="min-h-screen py-16">
      <EncabezadoUpim etiqueta="Facturación" titulo="Bandeja de solicitudes"
        descripcion="Solicitudes de factura generadas por pagos confirmados. Los datos fiscales sólo se muestran aquí."
        testId="bandeja-facturacion-encabezado" />

      <div className="mt-10 flex items-center gap-3">
        <label className="text-sm text-[var(--upim-texto-sec)]">Estado</label>
        <select className={campo} data-testid="bandeja-filtro-estado" value={estado}
          onChange={(e) => setEstado(e.target.value)}>
          {ESTADOS.map((s) => <option key={s || "todos"} value={s}>{s || "todos"}</option>)}
        </select>
        <span className="text-sm text-[var(--upim-texto-sec)]" data-testid="bandeja-total">
          {solicitudes.length} solicitud(es)
        </span>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1fr]">
        <TarjetaUpim testId="bandeja-lista-card">
          <ul className="space-y-3 text-sm" data-testid="bandeja-lista">
            {solicitudes.length === 0 && (
              <li className="text-[var(--upim-texto-sec)]">No hay solicitudes en este estado.</li>
            )}
            {solicitudes.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => abrir(s.id)}
                  data-testid={`bandeja-item-${s.id}`}
                  className="w-full rounded-lg border border-white/10 p-3 text-left transition-colors hover:border-[var(--upim-acento)]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[var(--upim-texto)]">{s.folio || s.id.slice(0, 8)}</span>
                    <span className="text-xs uppercase tracking-wide text-[var(--upim-acento)]">{s.estado}</span>
                  </div>
                  <p className="mt-1 text-[var(--upim-texto-sec)]">
                    {s.concepto || "—"} · {s.total} {s.moneda}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </TarjetaUpim>

        <TarjetaUpim testId="bandeja-detalle-card">
          {!detalle && (
            <p className="text-sm text-[var(--upim-texto-sec)]">Selecciona una solicitud.</p>
          )}
          {detalle && (
            <div className="space-y-4 text-sm" data-testid="bandeja-detalle">
              <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--upim-texto)] md:text-lg">
                <Receipt className="h-5 w-5 text-[var(--upim-acento)]" aria-hidden="true" />
                {detalle.folio || detalle.id.slice(0, 8)}
              </h2>
              <p className="text-[var(--upim-texto-sec)]">
                {detalle.concepto} · {detalle.total} {detalle.moneda} · estado{" "}
                <span className="text-[var(--upim-acento)]">{detalle.estado}</span>
              </p>

              {detalle.datos_fiscales ? (
                <dl className="grid grid-cols-2 gap-2 rounded-lg border border-white/10 p-3"
                  data-testid="bandeja-datos-fiscales">
                  {Object.entries(detalle.datos_fiscales).map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt className="text-[var(--upim-texto-sec)]">{k}</dt>
                      <dd className="truncate text-[var(--upim-texto)]">{String(v)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              ) : (
                <p className="text-[var(--upim-texto-sec)]" data-testid="bandeja-sin-datos-fiscales">
                  El usuario aún no ha completado su perfil fiscal.
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <BotonUpim variante="contorno" data-testid="bandeja-notificar"
                  onClick={() => notificar(detalle.id)}>
                  <Bell className="mr-2 h-4 w-4" aria-hidden="true" /> Notificar al contador
                </BotonUpim>
              </div>

              <div className="space-y-3 rounded-lg border border-white/10 p-3">
                <h3 className="text-sm font-semibold text-[var(--upim-texto)]">Registrar CFDI</h3>
                <input className={`${campo} w-full`} placeholder="UUID del CFDI"
                  data-testid="bandeja-uuid" value={uuid} onChange={(e) => setUuid(e.target.value)} />
                <label className="flex items-center gap-2 text-[var(--upim-texto-sec)]">
                  XML <input type="file" accept=".xml,application/xml,text/xml"
                    data-testid="bandeja-xml" onChange={(e) => setXml(e.target.files?.[0] || null)} />
                </label>
                <label className="flex items-center gap-2 text-[var(--upim-texto-sec)]">
                  PDF <input type="file" accept="application/pdf"
                    data-testid="bandeja-pdf" onChange={(e) => setPdf(e.target.files?.[0] || null)} />
                </label>
                <BotonUpim data-testid="bandeja-cargar-cfdi" onClick={() => cargarCfdi(detalle.id)}>
                  <Upload className="mr-2 h-4 w-4" aria-hidden="true" /> Registrar factura
                </BotonUpim>
              </div>

              {(detalle.archivos || []).length > 0 && (
                <div className="flex gap-3" data-testid="bandeja-archivos">
                  {detalle.archivos.map((a) => (
                    <span key={a.id} className="text-[var(--upim-texto-sec)]">
                      {a.tipo.toUpperCase()} · {a.sha256.slice(0, 10)}…
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </TarjetaUpim>
      </div>
    </SeccionUpim>
  );
}
